import type { ProductReviewSummary } from 'src/lib/trusted-shops/product-reviews';
import { getStructuredDataImages } from './images';

/** Prices use cents in the experience's currency, independent of cart currency. */
export type ExperienceStructuredOffer = {
  url: string;
  name?: string;
  priceInCents: number;
  availability?: `https://schema.org/${string}`;
  /** A visible "ab" price is the lowest of several prices, not one exact price. */
  isPriceFrom?: boolean;
};
type ExperienceStructuredDataProps = {
  experience: {
    publicId: number;
    title: string;
    currency: string;
    images?: {
      url: string;
    }[] | null;
    description?: string | null;
  };
  canonicalUrl?: string;
  offers?: readonly ExperienceStructuredOffer[];
  reviewSummary: ProductReviewSummary | null;
};
export function ExperienceStructuredData({
  experience,
  reviewSummary,
  canonicalUrl,
  offers = []
}: ExperienceStructuredDataProps) {
  const data: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: experience.title,
    sku: `E-${experience.publicId}`
  };
  if (canonicalUrl) data.url = canonicalUrl;
  const images = getStructuredDataImages(experience.images?.map(image => image.url) ?? []);
  if (images.length) data.image = images;
  if (experience.description) data.description = experience.description;
  const confirmedOffers = offers.filter(offer => Number.isFinite(offer.priceInCents) && offer.priceInCents >= 0);
  // Google requires offers or a qualifying rating/review, not a name-only Product.
  if (!confirmedOffers.length && !reviewSummary?.count) return null;
  if (confirmedOffers.length) {
    data.offers = confirmedOffers.map(offer_0 => ({
      '@type': offer_0.isPriceFrom ? 'AggregateOffer' : 'Offer',
      url: offer_0.url,
      ...(offer_0.name ? {
        name: offer_0.name
      } : {}),
      [offer_0.isPriceFrom ? 'lowPrice' : 'price']: offer_0.priceInCents / 100,
      priceCurrency: experience.currency,
      ...(offer_0.availability ? {
        availability: offer_0.availability
      } : {})
    }));
  }
  if (reviewSummary && reviewSummary.count > 0) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(reviewSummary.rating.toFixed(1)),
      bestRating: 5,
      reviewCount: reviewSummary.count
    };
  }
  const json = JSON.stringify(data).replace(/</g, '\\u003c');
  return <script type="application/ld+json" dangerouslySetInnerHTML={{
    __html: json
  }} />;
}